import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Animated,
  TouchableOpacity,
  NativeSyntheticEvent,
  NativeScrollEvent,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper, { WithColor } from '@styles/colors';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import { fullHeight, fullWidth, scale } from '@components/frame';

import { ModeSelector } from '@components/search/components/toolbar';
import Suggestion from '@components/search/components/suggestion';
import History from '@components/search/components/history';
import { isPhone, LEVELS } from '@helpers';
import { connect } from 'react-redux';
import { setBatchListContainerHeight, setBatchListYPosition } from '@reducers/batch';
import { ColorFunction } from '@styles/colors/types';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { SEARCH_HEIGHT } from './constants';
import { AnimatedHeaderProps } from './types';
import { calculateHeaderDimensions, getVisibilityFlags, isCloseToBottom } from './helpers';

const MobileHeader = <T,>(props: AnimatedHeaderProps<T>) => {
  const {
    title,
    C,
    displayMode,
    setSelectedDisplayMode,
    suggestion,
    onSearch,
    setSuggestion,
    searchRef,
    cancelAnimation,
    setSelectedNCFile,
    submit,
    searchValue,
    renderProps,
    loaded,
    isError,
    totalSearchResult,
    isAllLevel,
    alowLoadMore,
    setLoadMore,
    loadMore,
    level,
    setBatchListContainerHeight,
    setBatchListYPosition,
    noSearchScreen,
    width,
    isMachinesScreen,
    scrollListener: scrollListenerFromProps,
    isPopupMode = false,
    searchInputViewSelectorBg = 'Gray/Light/5',
  } = props;

  const { IS_NARROW_WEB } = useDeviceSize();

  const hideTitleSection = isPopupMode; // hide large title and found counter in popup

  const scrollY = useRef(new Animated.Value(0)).current;
  const inputFocus = useRef(new Animated.Value(0)).current;

  const listRef = useRef<ScrollView | null>(null);

  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputChanged, setInputChanged] = useState(false);

  const {
    NOTCH_TOP,
    HEADER_TEXT_HEIGHT,
    HEADER_HEIGHT,
    TITLE_HEIGHT,
    SPACE_BETWEEN_TITLE_AND_SEARCH,
    TOTAL_HEIGHT,
    TITLE_ANIMATION_POSITION,
    HEADER_ANIMATION_START,
  } = calculateHeaderDimensions(!!noSearchScreen, hideTitleSection);

  const searchBarScale = scrollY.interpolate({
    inputRange: [0, 1, SEARCH_HEIGHT, SEARCH_HEIGHT + 1],
    outputRange: [1, 1, 0, 0],
    extrapolate: 'extend',
  });

  const searchBarHeight = scrollY.interpolate({
    inputRange: [0, 1, SEARCH_HEIGHT, SEARCH_HEIGHT + 1],
    outputRange: [SEARCH_HEIGHT, SEARCH_HEIGHT, 0, 0],
    extrapolate: 'extend',
  });

  const searchBarTranslateY = scrollY.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [1, 0, 0],
    extrapolate: 'extend',
  });

  const titleTranslateY = scrollY.interpolate({
    inputRange: [-1, 0, TITLE_ANIMATION_POSITION, TITLE_ANIMATION_POSITION + 1],
    outputRange: [1, 0, 0, -1],
    extrapolate: 'extend',
  });

  const titleRoundOpacity = scrollY.interpolate({
    inputRange: [0, TITLE_ANIMATION_POSITION, TITLE_ANIMATION_POSITION + 1],
    outputRange: [1, 1, 0],
    extrapolate: 'extend',
  });

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, HEADER_ANIMATION_START, HEADER_ANIMATION_START + 1],
    outputRange: [0, 0, 1],
    extrapolate: 'extend',
  });

  const headerBorderColor = scrollY.interpolate({
    inputRange: [0, HEADER_ANIMATION_START, HEADER_ANIMATION_START + 1, HEADER_ANIMATION_START + 2],
    outputRange: [C('Background/Light/Header App'), C('Background/Light/Header App'), C('Hairline/Light/Header'), C('Hairline/Light/Header')],
    extrapolate: 'extend',
  });

  const scrollPosition = useRef<number>(0);

  const handleMomentumScrollEnd = (event?: NativeSyntheticEvent<NativeScrollEvent>) => {
    const currentOffset = event?.nativeEvent.contentOffset.y ?? scrollPosition.current;

    if (currentOffset < 5) {
      setShowPlaceholder(true);
    }

    if (currentOffset > 0 && currentOffset < SEARCH_HEIGHT + SPACE_BETWEEN_TITLE_AND_SEARCH) {
      if (currentOffset < SEARCH_HEIGHT - 2) {
        listRef?.current?.scrollTo({ y: 0, animated: true });
      } else {
        listRef?.current?.scrollTo({
          y: TITLE_ANIMATION_POSITION,
          animated: true,
        });
      }
    }

    if (!noSearchScreen && currentOffset > TITLE_ANIMATION_POSITION && currentOffset < HEADER_ANIMATION_START + SPACE_BETWEEN_TITLE_AND_SEARCH) {
      const space = HEADER_ANIMATION_START - TITLE_ANIMATION_POSITION;
      if (currentOffset < TITLE_ANIMATION_POSITION + space / 1.5) {
        listRef?.current?.scrollTo({
          y: TITLE_ANIMATION_POSITION,
          animated: true,
        });
      } else {
        listRef?.current?.scrollTo({
          y: HEADER_ANIMATION_START + SPACE_BETWEEN_TITLE_AND_SEARCH,
          animated: true,
        });
      }
    }
  };

  let scrollTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const scrollListener = async (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { nativeEvent } = event;
    scrollPosition.current = nativeEvent.contentOffset.y;

    if (level === LEVELS.ProductionBatches) {
      if (scrollTimeoutId.current) {
        clearTimeout(scrollTimeoutId.current);
      }

      scrollTimeoutId.current = setTimeout(() => {
        const y = nativeEvent.contentOffset.y;
        setBatchListYPosition(y);
        scrollTimeoutId.current = null;
      }, 100);
    }

    if (isCloseToBottom(nativeEvent) && alowLoadMore) {
      setLoadMore(false);
      await loadMore(() => {
        setTimeout(() => {
          setLoadMore(true);
        }, 1000);
      });
    }

    const currentOffset = nativeEvent.contentOffset.y;
    if (currentOffset < SEARCH_HEIGHT) {
      setShowPlaceholder(currentOffset < 5);
    }
  };

  const onInputFocus = () => {
    setInputFocused(true);
    Animated.timing(inputFocus, {
      toValue: 1,
      duration: 300,
      useNativeDriver: true,
    }).start();
    if (searchValue) {
      onSearch(searchValue);
    }
  };

  const onInputBlur = () => {
    setInputFocused(false);
    Animated.timing(inputFocus, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start();
  };

  const focusInputTranslateY = inputFocus.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -(HEADER_TEXT_HEIGHT + TITLE_HEIGHT - (noSearchScreen ? 0 : 7))],
    extrapolate: 'extend',
  });

  const focusContentOpacity = inputFocus.interpolate({
    inputRange: [0, 0.7, 1],
    outputRange: [0, 0, 1],
    extrapolate: 'extend',
  });

  const focusHeaderOpacity = inputFocus.interpolate({
    inputRange: [0, 0.4],
    outputRange: [1, 0],
    extrapolate: 'extend',
  });

  const { showHistory, showElements, hideModeSelector } = getVisibilityFlags({
    inputFocused,
    showPlaceholder,
    searchValue,
    inputChanged,
    isAllLevel,
    level,
  });

  return (
    <View
      style={{
        width: width ?? '100%',
        height: fullHeight,
        backgroundColor: C(noSearchScreen ? 'Background/Light/Details' : 'Background/Light/System'),
      }}
    >
      <View
        style={[
          { position: 'absolute', zIndex: 10 },
          inputFocused && {
            height: HEADER_HEIGHT,
          },
        ]}
      >
        {!noSearchScreen && (
          <View
            style={{
              width: '100%',
              height: HEADER_HEIGHT,
              backgroundColor: C(noSearchScreen ? 'Background/Light/Details' : 'Background/Light/System'),
              paddingTop: NOTCH_TOP,
              zIndex: 100,
            }}
          >
            <Animated.View
              style={{
                height: '100%',
                width: '100%',
                backgroundColor: C(noSearchScreen ? 'Background/Light/Details' : 'Background/Light/System'),
                borderBottomColor: headerBorderColor,
                borderBottomWidth: StyleSheet.hairlineWidth,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: scale(13),
                paddingRight: scale(20),
                opacity: focusHeaderOpacity,
              }}
            >
              <View style={{ flexDirection: 'row', width: scale(105) }}></View>
              <TouchableOpacity
                onPress={() => {
                  listRef?.current?.scrollTo({ y: 0, animated: true });
                }}
              >
                <Animated.View style={{ opacity: headerOpacity }}>
                  <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{title}</Text>
                </Animated.View>
              </TouchableOpacity>
              <View style={{ width: scale(100), alignItems: 'flex-end' }}></View>
            </Animated.View>
          </View>
        )}
        {!noSearchScreen && !hideTitleSection && totalSearchResult && !inputFocused && (
          <Animated.View
            style={{
              transform: [{ translateY: titleTranslateY }, { translateY: focusInputTranslateY }],
              position: 'absolute',
              left: scale(SCREEN_PADDING_HORIZONTAL),
              zIndex: 10000,
              top: HEADER_HEIGHT - 20,
              opacity: isAllLevel ? 0 : titleRoundOpacity,
            }}
          >
            <Text
              style={[
                {
                  color: C('Label/Light/Tertiary'),
                  marginRight: scale(20),
                  fontSize: scale(14),
                },
              ]}
            >
              {loaded && !isError ? totalSearchResult : ''} {i18n.t('FOUND')}
            </Text>
          </Animated.View>
        )}
        {!noSearchScreen && !hideTitleSection && (
          <Animated.View
            style={{
              paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
              height: TITLE_HEIGHT,
              flexDirection: 'row',
              opacity: focusHeaderOpacity,
              transform: [{ translateY: titleTranslateY }, { translateY: focusInputTranslateY }],
            }}
          >
            <Text
              style={[
                font['Large Title'],
                { color: C('Label/Light/Primary') },
              ]}
            >
              {title}
            </Text>
          </Animated.View>
        )}
        <Animated.View
          style={[
            {
              height: scale(inputFocused ? SEARCH_HEIGHT : noSearchScreen ? searchBarHeight : SEARCH_HEIGHT),
              paddingHorizontal: scale(noSearchScreen ? 0 : SCREEN_PADDING_HORIZONTAL),
              zIndex: 1000,
              flexDirection: 'row',
              width: IS_NARROW_WEB ? fullWidth : width ?? '100%',
              overflow: noSearchScreen ? 'hidden' : undefined,
            },
            inputFocused
              ? { transform: [{ translateY: focusInputTranslateY }] }
              : { transform: [{ translateY: searchBarTranslateY }, { scaleY: searchBarScale }] },
          ]}
        >
          <View style={[{ flex: 1 }, noSearchScreen && isMachinesScreen && { paddingLeft: scale(SCREEN_PADDING_HORIZONTAL) }]}>
            {showElements && (
              <View
                style={{
                  position: 'absolute',
                  left: scale(noSearchScreen && isMachinesScreen ? 22 : 8),
                  top: scale(noSearchScreen ? 11 : 10),
                  zIndex: 1000,
                }}
              >
                <View>
                  <Svg icon="search" color="Label/Light/Tertiary" width={scale(16)} height={scale(16)} />
                </View>
              </View>
            )}
            {showElements && searchValue && (
              <View
                style={{
                  position: 'absolute',
                  right: scale(8),
                  top: scale(10),
                  zIndex: 1000,
                }}
              >
                <TouchableOpacity
                  onPress={() => {
                    searchRef.current?.focus();
                    onSearch('');
                  }}
                  hitSlop={scale(10)}
                >
                  <Svg icon="clear" color="Label/Light/Tertiary" width={scale(16)} height={scale(16)} />
                </TouchableOpacity>
              </View>
            )}
            <TextInput
              ref={searchRef}
              style={{
                flex: 1,
                height: SEARCH_HEIGHT,
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                paddingLeft: scale(30),
                width: '100%',
                backgroundColor: C(searchInputViewSelectorBg),
                color: C('Label/Dark/Primary'),
              }}
              placeholder={showElements ? i18n.t('Search') : ''}
              placeholderTextColor={C('Label/Light/Tertiary')}
              onFocus={() => {
                setInputChanged(false);
                onInputFocus();
              }}
              onBlur={onInputBlur}
              returnKeyType="search"
              value={showElements ? searchValue : ''}
              onChangeText={(t) => {
                setInputChanged(true);
                onSearch(t);
              }}
              onSubmitEditing={() => {
                searchValue ? submit() : cancelAnimation();
              }}
            />
          </View>
          {!hideModeSelector && (
            <View style={[{ paddingLeft: scale(SCREEN_PADDING_HORIZONTAL) }, noSearchScreen && { paddingRight: scale(SCREEN_PADDING_HORIZONTAL) }]}>
              <ModeSelector {...{ C, displayMode, setSelectedDisplayMode, level }} />
            </View>
          )}
          {(inputFocused || searchValue) && (
            <TouchableOpacity
              onPress={() => {
                listRef?.current?.scrollTo({ y: 0, animated: true });
                searchRef.current?.blur();
                cancelAnimation();
              }}
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                paddingLeft: scale(SCREEN_PADDING_HORIZONTAL),
              }}
            >
              <View style={[noSearchScreen && { paddingRight: scale(SCREEN_PADDING_HORIZONTAL) }]}>
                <Text style={{ fontSize: scale(17), color: C('System/Light/Blue') }}>Cancel</Text>
              </View>
            </TouchableOpacity>
          )}
        </Animated.View>
      </View>
      {(() => {
        return showHistory ? (
          <Animated.View
            style={{
              paddingTop: HEADER_HEIGHT,
              flex: 1,
              opacity: focusContentOpacity,
            }}
          >
            <View style={{ flex: 1 }}>
              {(() => {
                if (searchValue) {
                  return (
                    <Suggestion
                      {...{
                        suggestion,
                        C,
                        onSearch,
                        setSuggestion,
                        searchRef,
                        cancelAnimation,
                        setSelectedNCFile,
                        ...(noSearchScreen ? { top: scale(35), tabletAsWeb: true } : {}),
                      }}
                    />
                  );
                } else {
                  return <History {...{ ...renderProps, fromHeader: true }} />;
                }
              })()}
            </View>
          </Animated.View>
        ) : (
          (() => {
            return (
              <Animated.ScrollView
                ref={listRef}
                onLayout={(event) => {
                  const { height } = event.nativeEvent.layout;
                  setBatchListContainerHeight(height);
                }}
                onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
                  useNativeDriver: false,
                  listener: scrollListenerFromProps ?? scrollListener,
                })}
                onMomentumScrollEnd={handleMomentumScrollEnd}
                onTouchEnd={() => {
                  if (Platform.OS === 'ios') {
                    setTimeout(() => {
                      handleMomentumScrollEnd();
                    }, 100);
                  }
                }}
                scrollEventThrottle={16}
                contentContainerStyle={{
                  paddingTop: inputFocused ? HEADER_HEIGHT : TOTAL_HEIGHT,
                  paddingBottom: scale(250),
                }}
              >
                <View>{props.children}</View>
              </Animated.ScrollView>
            );
          })()
        );
      })()}
    </View>
  );
};

export default connect(null, { setBatchListContainerHeight, setBatchListYPosition })(ColorWrapper(MobileHeader));
